import { service } from '@ember/service';
import BaseSessionService from 'ember-simple-auth/services/session';
import { DateTime } from 'scaleflow/utils/date-time';
import initializeMediaClient from 'scaleflow/utils/aws-clients/s3MediaClient';
import initializeVdrClient from 'scaleflow/utils/aws-clients/s3VDRClient';
import * as Sentry from '@sentry/ember';

// const INTERVAL = 50 * 60 * 1000; //50 minutes

export default class SessionService extends BaseSessionService {
  @service currentUser;
  @service store;
  @service organisation;
  @service roadmap;
  @service('data-socket') socketService;
  @service chatv2;
  @service betaToggle;
  @service router;

  refreshInProgress = null; // To keep track of the refresh process

  async handleAuthentication() {
    await this.currentUser.load();
    await this.organisation.load();
    await this.roadmap.setup();
    await this.socketService.setup();
    await this.chatv2.setup();
    await initializeMediaClient(this.idToken, this.organisation.id).catch(
      () => null,
    );
    if (this.betaToggle.showAiFeatures || this.betaToggle.isXrayCustomer)
      await initializeVdrClient(this.store).catch(() => null);
    const route = this.betaToggle.isSaasCustomer ? 'home' : 'scans';
    this.router.transitionTo(route);
  }

  async invalidate() {
    super.invalidate();
    localStorage.removeItem('ember_simple_auth-session');
    this.store.unloadAll();
    this.currentUser.clear();
    await this.socketService.close();
  }

  get accessToken() {
    return this.data?.authenticated?.details?.accessToken;
  }

  get idToken() {
    return this.data?.authenticated?.details?.idToken;
  }

  get tokenIsExpired() {
    const expiry = this.data?.authenticated?.details?.expiry;
    if (expiry === undefined) return false;
    const isExpired = DateTime.from(expiry).isSameOrBefore(DateTime.now());
    return isExpired;
  }

  get tokenIsHalfHourToExpiry() {
    const expiry = this.data?.authenticated?.details?.expiry;
    const halfHourToExpiry =
      DateTime.from(expiry).diff(DateTime.now(), 'minutes') <= 30;
    return halfHourToExpiry;
  }

  // Per-tab, not persisted in the shared auth session -- a background token
  // refresh must never let another tab's org switch bleed into this one.
  get activeTenantId() {
    const stored =
      window.sessionStorage.getItem('tenant') ??
      window.localStorage.getItem('tenant');
    const validTenants = this.data?.authenticated?.details?.tenants ?? [];
    return validTenants.includes(stored) ? stored : validTenants[0];
  }

  get clientId() {
    return this.data?.authenticated?.details?.clientId;
  }

  async refreshToken() {
    Sentry.captureMessage('Refreshing session token');
    if (this.refreshInProgress) {
      // If a refresh is already in progress, wait for it to complete
      return this.refreshInProgress;
    }
    const authenticator = this.data?.authenticated?.authenticator;
    if (!authenticator)
      return Promise.reject('Authenticator could not be determined');
    this.refreshInProgress = new Promise((resolve, reject) => {
      this.authenticate(authenticator, {
        refresh: true,
      })
        .then(() => {
          this.refreshInProgress = null;
          Sentry.captureMessage('Session refreshed succesfully');
          resolve();
        })
        .catch((err) => {
          console.debug(err);
          this.refreshInProgress = null;
          Sentry.captureMessage('Session refresh failed', {
            attributes: {
              expiresAt: this.data?.authenticated?.details?.expiry,
              now: DateTime.now().toISOString(),
              terminal: err?.isTerminal ?? false,
            },
          });
          // Propagate the error so callers can distinguish a genuinely dead
          // session (terminal -> log out) from a transient failure (keep the
          // session). Bare reject() used to make every failure look fatal.
          reject(err);
        });
    });

    return this.refreshInProgress;
  }
}
